import React, {useEffect, useCallback} from 'react';

import {Tracker} from '@udemy/event-tracking';
import {useI18n} from '@udemy/i18n';
import {OnePaneLayoutFullWidthSection} from '@udemy/react-layout-components';
import {
    PersonalPlanBanner as PersonalPlanBannerComponent,
    PersonalPlanBannerItemProps,
    SubscriptionOfferPresented,
    SubscriptionLearnMoreEvent,
} from '@udemy/subscription-browse';
import {
    MonthlySubscriptionPlanPricingOption,
    SubscriptionPlan,
    SubscriptionPlanProductType,
    useConsumerSubscriptionPlan,
} from '@udemy/subscription-graphql';

import styles from './personal-plan-banner.module.less';

/**
 * Personal Plan Banner Wrapper
 */
const PersonalPlanBannerWrapper = ({children}: {children: React.ReactNode}) => (
    <OnePaneLayoutFullWidthSection className={styles['personal-plan-banner-wrapper']}>
        <OnePaneLayoutFullWidthSection.Content>{children}</OnePaneLayoutFullWidthSection.Content>
    </OnePaneLayoutFullWidthSection>
);

/**
 * Personal Plan Banner Implementation
 */
const PersonalPlanBannerFeature = ({
    monthlyPlanOption,
    subscriptionPlan,
}: {
    monthlyPlanOption: MonthlySubscriptionPlanPricingOption;
    subscriptionPlan: SubscriptionPlan;
}): JSX.Element => {
    const {gettext} = useI18n();
    const subscriptionPrice = parseFloat(monthlyPlanOption.listPrice.amount);

    const features: PersonalPlanBannerItemProps[] = [
        {
            glyph: 'trophy',
            text: gettext('<span class="firstWord">Prep</span> for a certification'),
            bgColor: 'color-green-150',
        },
        {
            glyph: 'sparkles',
            text: gettext('<span class="firstWord">Learn</span> essential AI skills'),
            bgColor: 'color-purple-150',
        },
        {
            glyph: 'question-answer-outline',
            text: gettext('<span class="firstWord">Practice</span> with AI Role Play'),
            bgColor: 'color-orange-150',
        },
        {
            glyph: 'lightbulb',
            text: gettext('<span class="firstWord">Advance</span> your career'),
            bgColor: 'color-teal-150',
        },
    ];

    const onClick = useCallback(() => {
        Tracker.publishEvent(
            new SubscriptionLearnMoreEvent(
                monthlyPlanOption.id,
                SubscriptionPlanProductType.Consumersubscription,
                'get_certs_module',
                [monthlyPlanOption.id],
                null,
            ),
        );
    }, [monthlyPlanOption]);

    useEffect(() => {
        Tracker.publishEvent(
            new SubscriptionOfferPresented({
                subscriptionPlan,
                productType: SubscriptionPlanProductType.Consumersubscription,
                countryCode: monthlyPlanOption.listPrice.currency ?? 'USD',
                uiRegion: 'get_certs_module',
            }),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PersonalPlanBannerComponent
            title={gettext('Get certification courses and so much more with Personal Plan')}
            description={gettext('It’s always-on learning to help you reimagine your career')}
            features={features}
            subscriptionPrice={subscriptionPrice}
            ctaUrl="/personal-plan/"
            onCtaClick={onClick}
        />
    );
};

/**
 * PersonalPlanBanner
 */
export const PersonalPlanBanner = (): JSX.Element => {
    const personalPlanContext = useConsumerSubscriptionPlan();
    const isLoading = Boolean(personalPlanContext?.isLoading);

    if (isLoading) {
        return (
            <PersonalPlanBannerWrapper>
                <PersonalPlanBannerComponent isLoading={isLoading} />
            </PersonalPlanBannerWrapper>
        );
    }

    const monthlyPlanOption =
        personalPlanContext && 'monthlyPlanOption' in personalPlanContext
            ? personalPlanContext.monthlyPlanOption
            : undefined;

    // Early return prevents side effects when PP is not available
    if (monthlyPlanOption?.listPrice?.amount == null) {
        return <></>;
    }

    return (
        <PersonalPlanBannerWrapper>
            <PersonalPlanBannerFeature
                subscriptionPlan={personalPlanContext as SubscriptionPlan}
                monthlyPlanOption={monthlyPlanOption as MonthlySubscriptionPlanPricingOption}
            />
        </PersonalPlanBannerWrapper>
    );
};
